import type {ToolCallCancelledState, ToolCallState} from '@microsoft/agent-host-protocol'
import {useCallback, useMemo, useRef, type FocusEvent, type ReactNode} from 'react'
import type {SubagentResolver} from '@github-ui/ahp-relay/ahp-session-selectors'
import {ToolRenderer} from '../../tool/Tool'
import {ToolDefaultOpenContext} from '../../tool/ToolPrimitives'
import {ahpToolCallToToolProps} from '../../../../ahp/ahp-tool-helpers'
import {useAhpSubagent} from '../../../../ahp/use-ahp-child-session-state'
import {useSessionContext} from '../../../../contexts/SessionContext'
import {AhpPermission} from './AhpPermission'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ToolFooterSlotContext} from '../../tool/ToolFooterSlot'
import {
  ahpToolTimingKey,
  useAhpToolTiming,
  type AhpToolPhase,
  type AhpToolTiming,
} from '../../../../ahp/use-ahp-tool-timing'
import {AhpToolCallFooter, hasAhpToolCallFooterContent} from './AhpToolCallFooter'
import type {AhpSubagentComponent} from './AhpSubagentReference'

const STATUS_CANCELLED = 'cancelled'
const STATUS_COMPLETED = 'completed'
const STATUS_RUNNING = 'running'
const STATUS_PENDING_RESULT_CONFIRMATION = 'pending-result-confirmation'
const CANCEL_REASON_DENIED = 'denied'
const CANCEL_REASON_RESULT_DENIED = 'result-denied'

export function isDeniedCancellation(toolCall: ToolCallState): toolCall is ToolCallCancelledState {
  return (
    toolCall.status === STATUS_CANCELLED &&
    (toolCall.reason === CANCEL_REASON_DENIED || toolCall.reason === CANCEL_REASON_RESULT_DENIED)
  )
}

const TOOL_CALL_TIMING_FLAG = 'copilot_ahp_tool_call_timing'
// Tools that render as inline notes or completion markers rather than a tool card.
const TOOLS_WITHOUT_FOOTER = new Set(['think', 'report_intent', 'task_complete'])

export function toolPhase(status: ToolCallState['status']): AhpToolPhase {
  if (status === STATUS_RUNNING) return 'executing'
  if (status === STATUS_COMPLETED || status === STATUS_CANCELLED || status === STATUS_PENDING_RESULT_CONFIRMATION) {
    return 'done'
  }
  return 'waiting'
}

const TIMELINE_SELECTOR = '[data-testid="ahp-session-timeline"]'
const TURN_SELECTOR = '[data-turn-id]'
const TURN_REMOVAL_FRAMES = 3

/**
 * Timeline markers sit between turns as list items without a turn id, so the
 * nearest surviving turn is not always an immediate sibling.
 */
function nearestTurns(turn: HTMLElement): HTMLElement[] {
  const found: HTMLElement[] = []
  for (const step of ['previousElementSibling', 'nextElementSibling'] as const) {
    let sibling = turn[step]
    while (sibling) {
      if (sibling instanceof HTMLElement && sibling.matches(TURN_SELECTOR)) {
        found.push(sibling)
        break
      }
      sibling = sibling[step]
    }
  }
  return found
}

/**
 * Removing the focused element drops focus to the body, so recovery only runs
 * while nothing else has claimed it.
 */
function focusIsLost() {
  const active = document.activeElement
  return !active || active === document.body || active === document.documentElement
}

/**
 * A turn whose only visible content was the subagent is removed along with it, so
 * focusing the turn leaves focus on the body once React commits the unmount. The
 * relay notifies before React re-renders, so the removal can land several frames
 * later, and focus stays in the list when a neighboring turn survives.
 */
function focusAfterTurnRemoval(turn: HTMLElement | null | undefined, timeline: HTMLElement | null | undefined) {
  if (!turn) return
  const neighbors = nearestTurns(turn)
  let remainingFrames = TURN_REMOVAL_FRAMES

  const attempt = () => {
    if (turn.isConnected) {
      if (remainingFrames-- > 0) requestAnimationFrame(attempt)
      return
    }
    if (!focusIsLost()) return
    const replacement = neighbors.find(candidate => candidate.isConnected)
    if (replacement) {
      replacement.focus()
      return
    }
    if (timeline?.isConnected) timeline.focus()
  }

  requestAnimationFrame(attempt)
}

/**
 * Pending confirmations are displayed as tool activity, while the confirmation
 * choices are handled by the host app's composer surface. Denied calls are
 * surfaced as permission entries, subagent calls as subagent references, and
 * other states use `ToolRenderer`.
 */
export function AhpToolCall({
  toolCall,
  chatResource,
  SubagentComponent,
  subagentOffline,
  subagentChatsEnabled,
  resolveSubagent,
  defaultExpanded,
}: {
  toolCall: ToolCallState
  chatResource?: string
  SubagentComponent: AhpSubagentComponent
  subagentOffline?: boolean
  subagentChatsEnabled: boolean
  resolveSubagent?: SubagentResolver
  defaultExpanded?: boolean
}) {
  const {session} = useSessionContext()
  const persistedSubagentHasFocusRef = useRef(false)
  const subagentContainerRef = useRef<HTMLDivElement | null>(null)
  const restoreFocus = useCallback(() => {
    const container = subagentContainerRef.current
    const activeElement = document.activeElement
    if (!container || !(activeElement instanceof HTMLElement)) return
    const turn = container.closest<HTMLElement>(TURN_SELECTOR)
    if (!turn?.contains(activeElement)) return
    if (container.contains(activeElement)) turn.focus()
    focusAfterTurnRemoval(turn, turn.closest<HTMLElement>(TIMELINE_SELECTOR))
  }, [])
  const attachLiveSubagentContainer = useCallback((node: HTMLDivElement | null) => {
    subagentContainerRef.current = node
  }, [])
  const attachPersistedSubagentContainer = useCallback((node: HTMLDivElement | null) => {
    subagentContainerRef.current = node
    if (!node) return
    const turn = node.closest<HTMLElement>(TURN_SELECTOR)
    const turnId = turn?.getAttribute('data-turn-id')
    const timeline = turn?.closest<HTMLElement>(TIMELINE_SELECTOR)
    const neighbors = turn ? nearestTurns(turn) : []

    return () => {
      if (!persistedSubagentHasFocusRef.current) return
      persistedSubagentHasFocusRef.current = false
      requestAnimationFrame(() => {
        if (!focusIsLost()) return
        const replacementTurn =
          turnId && !timeline?.isConnected
            ? [...document.querySelectorAll<HTMLElement>(TURN_SELECTOR)].find(
                candidate => candidate.getAttribute('data-turn-id') === turnId,
              )
            : undefined
        const target =
          (turn?.isConnected ? turn : replacementTurn) ?? neighbors.find(candidate => candidate.isConnected)
        if (target) {
          target.focus()
          return
        }
        if (timeline?.isConnected) timeline.focus()
      })
    }
  }, [])
  const handlePersistedSubagentFocus = useCallback(() => {
    persistedSubagentHasFocusRef.current = true
  }, [])
  const handlePersistedSubagentBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget
    persistedSubagentHasFocusRef.current = nextTarget instanceof Node && event.currentTarget.contains(nextTarget)
  }, [])
  const liveSubagent = useAhpSubagent(
    session.task_id,
    chatResource,
    toolCall,
    restoreFocus,
    subagentChatsEnabled && !resolveSubagent,
  )
  const subagent = resolveSubagent ? resolveSubagent(toolCall, chatResource) : liveSubagent
  const showFooter = isFeatureEnabled(TOOL_CALL_TIMING_FLAG) && !TOOLS_WITHOUT_FOOTER.has(toolCall.toolName)
  const phase = toolPhase(toolCall.status)
  // Tool call ids are only unique within a chat, and the timing store is shared across chats.
  const timingKey = ahpToolTimingKey(session.task_id, chatResource, toolCall.toolCallId)
  const timing = useAhpToolTiming(timingKey, phase, showFooter)

  if (isDeniedCancellation(toolCall)) {
    return <AhpPermission toolCall={toolCall} />
  }

  if (subagent) {
    return (
      <div
        ref={resolveSubagent ? attachPersistedSubagentContainer : attachLiveSubagentContainer}
        data-subagent-tool-call-id={toolCall.toolCallId}
        onFocusCapture={resolveSubagent ? handlePersistedSubagentFocus : undefined}
        onBlurCapture={resolveSubagent ? handlePersistedSubagentBlur : undefined}
      >
        <SubagentComponent subagent={subagent} offline={subagentOffline} />
      </div>
    )
  }

  const {name, args, delta, isMcpTool, executionState} = ahpToolCallToToolProps(toolCall)
  const renderer = (
    <ToolDefaultOpenContext value={defaultExpanded}>
      <ToolRenderer name={name} args={args} delta={delta} isMcpTool={isMcpTool} executionState={executionState} />
    </ToolDefaultOpenContext>
  )
  if (!showFooter) return renderer

  return (
    <WithAhpToolCallFooter timing={timing} settled={phase === 'done'} output={delta.content}>
      {renderer}
    </WithAhpToolCallFooter>
  )
}

function WithAhpToolCallFooter({
  timing,
  settled,
  output,
  children,
}: {
  timing: AhpToolTiming | undefined
  settled: boolean
  output: string | undefined
  children: ReactNode
}) {
  const footer = useMemo(
    () =>
      hasAhpToolCallFooterContent({timing, settled, output}) ? (
        <AhpToolCallFooter timing={timing} settled={settled} output={output} />
      ) : undefined,
    [timing, settled, output],
  )
  return <ToolFooterSlotContext value={footer}>{children}</ToolFooterSlotContext>
}
